import { Component, ViewChild, NgZone, ChangeDetectorRef, ViewEncapsulation, TemplateRef, Output, EventEmitter } from '@angular/core';
import { EnpsSinglePageComponent } from '@common/components/base/enps-single-page';

import { TimeoutQueue } from '@common/services/timeout-queue';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '@suite/app.service';
import { AuthService } from '@common/services/authentication/auth.service';

import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { UserGroup } from '@common/data-models/search-results/result-group';
import { UserStaffInfo } from '@common/data-models/service/user-staff-info';
import { DataStore } from '@common/data-models/data-store/data-store';
import { UsersResults } from '@common/data-models/search-results/users-result';
import { GlobalRoles, InvitationStatusCodes } from '@common/utils/util';

import { ModalResponseCodes, ModalService } from '../modal/modal.service';

import { GlobalTablesService } from "@common/services/global-tables-service";
import { LanguageSettings } from "@common/services/language-settings";
import { NavigationService } from "@common/services/navigation-service";
import { TeamMembersService } from "@common/services/team-members-service";
import { VisualStateService } from '@suite/common/services/visual-state-service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddTeamMember } from './team-member-item/add-team-member';
import { take } from 'rxjs/operators';

declare var virtualDirectory: string;

@Component({
    selector: 'manage-team-members',
    templateUrl: './manage-team-members.html',
    styleUrls: ['./manage-team-members.scss', '../base/enps-single-page.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        'class': 'st-manage-team-members'
    }
})

export class ManageTeamMembers extends EnpsSinglePageComponent {
    public predefineFilters: { [id: string]: Array<{ id: string, title: string }> };
    public readonly PSTFieldNames = PSTFieldNames;
    public currentRole;
    public defaultAvatarLocation = "../" + (('virtualDirectory' in window) ? virtualDirectory : "/src") + "src/assets/images/PSTDefaultAvatar.png";
    @Output() public changed: EventEmitter<UserStaffInfo | string> = new EventEmitter<any>();

    public roles;
    public programs = [];

    public get groupByField(): string {
        return UsersResults.groupByField;
    }

    public get sortByField(): any {
        return UsersResults.sortByField;
    }
    public set sortByField(v: any) {
        if (v != UsersResults.sortByField) {
            this.timeoutQueue.setTimeout(() => {
                UsersResults.sortByField = v;
                if (DataStore.usersResults.users.length > 0 || DataStore.usersResults.newUsers.length > 0) {
                    DataStore.usersResults.sortGroups(v)
                    DataStore.usersResults.rebuildGroups(this.groupByField, this.sortByField)
                }
            }, 0, this.changeDetectorRef);
        }
    }
    public InvitationStatusCodes: any = InvitationStatusCodes;
    public currentTabIndex: number = 0;
    public currentGroup: string = '';

    @ViewChild('mainCta', { read: TemplateRef, static: true }) mainCta;
    @ViewChild('viewOptions', { read: TemplateRef, static: true }) viewOptions;
    @ViewChild('rightSlot', { read: TemplateRef, static: true }) rightSlot;
    public displayedColumns: string[] = ['Avatar', 'ID', 'First Name', 'Last Name', 'Email', 'SSO Logins', 'Default Team', 'Role', 'Status', 'Invitation Status', 'Delete'];
    public teamMemberDialogRef: MatDialogRef<AddTeamMember, any> = null;

    pageSize = 20;
    pageIndex = 0;
    hidePageSize = true;
    showFirstLastButtons = true;

    constructor(
        public appService: AppService,
        protected modalService: ModalService,
        protected ngZone: NgZone,
        protected changeDetectorRef: ChangeDetectorRef,
        timeoutQueue: TimeoutQueue,
        authService: AuthService,
        protected activatedRoute: ActivatedRoute,
        public globalTablesService: GlobalTablesService,
        public navigationService: NavigationService,
        public languageSettings: LanguageSettings,
        public teamMembersService: TeamMembersService,
        public visualStateService: VisualStateService,
        private dialog: MatDialog
    ) {
        super(appService, modalService, ngZone, changeDetectorRef, timeoutQueue, authService, activatedRoute, globalTablesService, navigationService, languageSettings);
        this.predefineFilters = {
            "grouping": [
                { id: PSTFieldNames.UserInfoStatus, title: this.languageSettings.PSTWebUserGroupSortByStatusLabel },
                { id: PSTFieldNames.UserInfoProgram, title: this.languageSettings.PSTWebUserGroupSortByProgramLabel },
                { id: PSTFieldNames.UserInfoRole, title: this.languageSettings.PSTWebUserGroupSortByRoleLabel }
            ],
            "sorting": [
                { id: PSTFieldNames.UserInfoID, title: this.languageSettings.PSTWebUserGroupSortByIDLabel },
                { id: PSTFieldNames.UserInfoProgram, title: this.languageSettings.PSTWebUserGroupSortByProgramLabel },
                { id: PSTFieldNames.UserInfoGivenName, title: this.languageSettings.PSTWebUserGroupSortByFirstNameLabel },
                { id: PSTFieldNames.UserInfoSurname, title: this.languageSettings.PSTWebUserGroupSortByLastNameLabel },
                { id: PSTFieldNames.UserInfoRole, title: this.languageSettings.PSTWebUserGroupSortByRoleLabel },
                { id: PSTFieldNames.UserInfoEmail, title: this.languageSettings.PSTWebUserGroupSortByEmailLabel },
                { id: PSTFieldNames.UserInfoStatus, title: this.languageSettings.PSTWebUserGroupSortByStatusLabel }
            ]
        };
    }

    public async ngOnInit() {
        this.generateProgramsArray();
        this.navigationService.currentListView = this;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        DataStore.usersResults.dispose();

    }

    ngAfterContentInit(): void {
        this.appService.viewStateService.mainCta = this.mainCta;
        this.appService.viewStateService.viewOptions = this.viewOptions;
        this.appService.viewStateService.rightSlot = this.rightSlot;
        this.appService.viewStateService.secondaryCta = null;
    }

    private async loadTeamMembersData() {
        this.showLoader();
        let res = await this.teamMembersService.getTeamMembers();

        if (res.status) {
            DataStore.usersResults = res.data.userResults;
            DataStore.usersResults.rebuildGroups(this.groupByField, this.sortByField);
            this.proccessSecurity(res.data.security);
            this.setCurrentRole(res.data.userResults.newUsers)
            this.setCurrentRole(res.data.userResults.users)
            this.loadCurrentGroup();
        }
        this.hideLoader();
    }

    async openNewMemberDialog() {
        this.dialog.closeAll();
        await this.dialog.afterAllClosed.pipe(take(1)).toPromise()
        this.teamMemberDialogRef = this.dialog.open(AddTeamMember, {
            width: '35rem',
            hasBackdrop: true,
            panelClass: 'st-add-team-member-dialog',
            data: { data: this.roles }
        });
    }

    private proccessSecurity(security: Array<any>) {
        if (security) {
            let roles = JSON.parse(JSON.stringify(security));
            roles.unshift(GlobalRoles.SUPERVISOR_ITEM);
            roles.unshift(GlobalRoles.SERVER_MANAGER_ITEM);
            roles.unshift(GlobalRoles.RESTRICTED_USER_ITEM);

            this.roles = roles;
        }
    }

    private setCurrentRole(users: Array<UserStaffInfo>) {
        users.forEach(user => {
            if (user.Supervisor) {
                user.GlobalRole = GlobalRoles.SUPERVISOR_ID;
            }
        })
    }

    private loadCurrentGroup() {
        let entries = this.userGroups[this.groupByField]?.keys;
        this.currentTabIndex = 0;
        this.currentGroup = entries[this.currentTabIndex]?.value;
    }

    private generateProgramsArray() {
        this.programs = [];
        this.loadTeamMembersData();
        if (this.globalTablesService.Groups) {
            for (let program in this.globalTablesService.Groups) {
                this.programs.push(this.globalTablesService.Groups[program]);
            }
        }
    }

    public get userGroups(): { [id: string]: UserGroup<UserStaffInfo> } {
        return DataStore.usersResults?.usersGroups;
    }

    public onPageChanged(group: string, event: any) {
        this.userGroups[this.groupByField].setCurrentPage(group, event.pageIndex);
    }

    public onTabChanged(event: any) {
        let entries = this.userGroups[this.groupByField]?.keys
        this.currentTabIndex = event.index
        this.currentGroup = entries[this.currentTabIndex]?.value
    }

    public async deleteItem(member: UserStaffInfo) {
        let modalRef = this.modalService.openModal(this.languageSettings.PSTWebConfirmDeleteUserLabel, this.languageSettings.PSTWebConfirmDeleteUserMessage + member.UserID, "Yes", "No");
        let modalRes;
        try {
            modalRes = await modalRef.result;
        } catch (er) {
            modalRes = ModalResponseCodes.CANCEL;
        }
        if (modalRes == ModalResponseCodes.OK) {
            try {
                this.loading = true;
                let res = await this.teamMembersService.removeTeamMember(member.UserID);
                if (res.status) {
                    DataStore.usersResults.removeUser(member);
                    DataStore.usersResults.rebuildGroups(UsersResults.groupByField, UsersResults.sortByField);
                    this.loadCurrentGroup();
                }
                this.loading = false;
            } catch (e) {
                this.loading = false;
            }
        } else {
            console.log("no");
        }
    }

    public async sendInvitation(member: UserStaffInfo) {
        let res = await this.teamMembersService.inviteTeamMember(member.UserID, member.Email, this.appService.nomWebServicesJson.PSTServer)
        if (res.status) {
            member.InvitationStatus = InvitationStatusCodes.PENDING;
            DataStore.usersResults.removeUser(member)
            DataStore.usersResults.addUser(member)
            DataStore.usersResults.rebuildGroups(UsersResults.groupByField, UsersResults.sortByField);
        }
    }

    public async saveMember(member: UserStaffInfo) {
        try {
            let res = await this.teamMembersService.updateTeamMember(member);
            if (res.status) {
                member = Object.assign(new UserStaffInfo, res.data);
                if (this.appService.nomWebServicesJson.HelloResults.UserStaffInfo.UserID == member.UserID) {
                    this.appService.nomWebServicesJson.HelloResults.UserStaffInfo = Object.assign(new UserStaffInfo, member);
                }
                DataStore.usersResults.updateUser(member)
                DataStore.usersResults.rebuildGroups(UsersResults.groupByField, UsersResults.sortByField);
            } else {
                //this.restoreState();
            }
        } catch (e) {
            //this.restoreState();
        }
    }

    valueChanged(member: UserStaffInfo, value: any, prop?: string) {
        this.changed.emit(prop);
        if (prop) {
            member[prop] = value;
        }
        this.saveMember(member);
    }

    public selectRole(member: UserStaffInfo) {
        if (member.GlobalRole == GlobalRoles.SUPERVISOR_ID) {
            member.Supervisor = true;
        } else {
            member.Supervisor = false;
            member.ServerManager = false;
            // member.GlobalRole = this.currentRole.ID;
        }
        this.saveMember(member);
    }

    public invitationButtonLabel(member: UserStaffInfo) {
        if (member.InvitationStatus == InvitationStatusCodes.ACTIVE) {
            return this.languageSettings.PSTWebSendedInvitationButtonLabel;
        } else {
            return this.languageSettings.PSTWebSendInvitationButtonLabel;
        }
    }
}
